import * as React from "react";
import { Store, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useUsersSearchQuery } from "./useUsers";
import { getFileUrl } from "@shared/utils/fileUrl";
import { AsyncSelect } from "@shared/components/AsyncSelect";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@shared/components/ui/avatar";
import { Badge } from "@shared/components/ui/badge";

export function SelectUser({
  onSelectUser,
  user,
  className,
  showDetails = true,
  filter = {},
  role = "client", // New role prop
  query = {}, // New query prop for additional search parameters
  showAllOption = false, // New prop to show "All" option
  defaultUser = null,
  clientSideSearch = false,
}) {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(user || "");

  const handleValueChange = (newValue) => {
    setValue(newValue);
  };

  const handleItemSelect = (selectedUser) => {
    // Handle "All" option case
    if (!selectedUser) {
      onSelectUser("");
    } else {
      onSelectUser(selectedUser?._id || "", selectedUser);
    }
  };

  const getUserSearchText = (user) => {
    return `${user.brandName} ${user.email || ""} ${user.phoneNumber || ""}`;
  };

  // Enhanced label formatting with user details
  const formatUserLabel = (user) => {
    if (showDetails) {
      return (
        <div className="flex items-center space-x-3 w-full">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage
              src={getFileUrl(user.profileImage)}
              alt={user.brandName}
            />
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              {user.brandName?.charAt(0)?.toUpperCase() ||
                user.firstName?.charAt(0)?.toUpperCase() ||
                "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <span className="font-medium text-foreground truncate">
                {user.brandName}
              </span>
              {user.role && (
                <Badge
                  variant={
                    user.role === "admin"
                      ? "destructive"
                      : user.role === "manager"
                        ? "secondary"
                        : user.role === "livreur"
                          ? "default"
                          : "outline"
                  }
                  className="text-xs"
                >
                  {t(`user.role.${user.role}`)}
                </Badge>
              )}
            </div>
            <div className="flex items-center space-x-3 text-xs text-muted-foreground mt-1">
              {user.email && (
                <span className="flex items-center">
                  <Mail className="h-3 w-3 mr-1" />
                  {user.email}
                </span>
              )}
              {user.phoneNumber && (
                <span className="flex items-center">
                  <Phone className="h-3 w-3 mr-1" />
                  {user.phoneNumber}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    }
    return user.brandName;
  };

  // Enhanced selected label format
  const formatSelectedLabel = (user) => {
    return (
      <div className="flex items-center space-x-2">
        <Avatar className="h-6 w-6">
          <AvatarImage src={user.profileImage} alt={user.brandName} />
          <AvatarFallback className="bg-primary/10 text-primary text-xs">
            {user.brandName?.charAt(0)?.toUpperCase() ||
              user.firstName?.charAt(0)?.toUpperCase() ||
              "U"}
          </AvatarFallback>
        </Avatar>
        <span className="font-medium truncate">{user.brandName}</span>
        {user.role && (
          <Badge
            variant={
              user.role === "admin"
                ? "destructive"
                : user.role === "manager"
                  ? "secondary"
                  : user.role === "livreur"
                    ? "default"
                    : "outline"
            }
            className="text-xs"
          >
            {t(`user.role.${user.role}`)}
          </Badge>
        )}
      </div>
    );
  };

  // Merge role filter with existing filter and query
  const searchFilter = React.useMemo(() => {
    const mergedFilter = { ...filter, ...query };
    if (role) {
      mergedFilter.role = role;
    }
    return mergedFilter;
  }, [filter, role, query]);

  return (
    <AsyncSelect
      value={value}
      onValueChange={handleValueChange}
      onItemSelect={handleItemSelect}
      defaultItem={defaultUser}
      placeholder={t("action.select", {
        entity: role ? t(`user.role.${role}`) : t("user.brandName"),
      })}
      searchPlaceholder={t("action.search", {
        entity: role ? t(`user.role.${role}`) : t("user.brandName"),
      })}
      notFoundMessage={t("main.notFound", {
        entity: role ? t(`user.role.${role}`) : t("user.brandName"),
      })}
      icon={<Store className="text-green-500" />}
      useSearchQuery={useUsersSearchQuery}
      queryParams={searchFilter}
      extractItems={(response) => response?.data || []}
      getItemKey={(user) => user._id}
      getItemValue={(user) => user._id}
      getItemLabel={formatUserLabel}
      formatSelectedLabel={formatSelectedLabel}
      debounceDelay={300}
      searchLimit={clientSideSearch ? 1000 : 50}
      className={className}
      showAllOption={showAllOption}
      allOptionLabel={t("main.all")}
      allOptionValue=""
      clientSideSearch={clientSideSearch}
      getItemSearchText={getUserSearchText}
    />
  );
}
